// Quote Builder — Apprentice Kit reasoning capture.
//
// Captures WHY a pricing/design decision was made, as Steve makes it, into
// qb_rationale (migration 0089). This is the foundational capture surface for
// the Apprentice Kit: Kit reads the store later, structurally, to answer "what
// did we do last time" — so what matters here is that the note is attached to
// the right subject, not that it reads well.
//
// ⚠️ INTERNAL ONLY. Nothing here may ever surface in the customer portal or
// the customer PDF. The rows are commercial reasoning ("gave 5% back to win
// the fitting work"), and the table is RLS-isolated per retailer for that
// reason. Capture only — no retrieval/consumption logic lives here.
//
// Two ways in:
//   1. Manual — the "Why" control on every line, always available.
//   2. Anomaly nudge — when a line is hand-priced away from its calculated
//      value, the control pulses to invite a note. Dismissible, never
//      blocking. This is capture-when-natural, not a compliance gate.

import { svgIcon } from "./formatters";
import type { QuoteItem } from "./types";

const BASE = "/api/portal/qb-rationale";

/** Speech-bubble with a question mark — "why?". 24×24, 1.5px, per the
 *  platform icon rule (custom SVG, no icon library). */
export const WHY_ICON =
  "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
  + "|M10.1 9.4a2 2 0 0 1 3.9.6c0 1.3-2 2-2 2"
  + "|M12 15.5h.01";

export interface RationaleRow {
  id: string;
  itemId: string | null;
  zoneId: string | null;
  body: string;
  source: "manual" | "anomaly";
}

// ── Per-quote cache ─────────────────────────────────────────────────────────
// itemId → the most recent note on that line. The API returns newest-first, so
// the first row seen for a line wins. Only one note per line is editable from
// the UI in v1; older rows for the same line stay in the store for Kit.
const byItem = new Map<string, RationaleRow>();

// Lines currently showing an anomaly nudge, and lines where the nudge has been
// dismissed. Both transient and deliberately outside the quote store — this is
// UI state, not business data, and it must not ride along into the saved quote
// or localStorage. Mirrors the `expandedLines` pattern in quote-area.ts.
const nudged = new Set<string>();
const dismissed = new Set<string>();
// Lines that raised a nudge at any point this session. Distinct from `nudged`,
// which is cleared the moment the editor opens: by save time the pulse is gone,
// but we still need to know the note was PROMPTED rather than volunteered —
// source='anomaly' vs 'manual' is evidence Kit weighs differently.
const promptedEver = new Set<string>();
// Which line has its editor open. Only one at a time keeps the row height
// stable while pricing.
let openFor: string | null = null;

let loadedQuoteKey = "";

export function hasRationale(itemId: string): boolean {
  return byItem.has(itemId);
}
export function isNudged(itemId: string): boolean {
  return nudged.has(itemId) && !dismissed.has(itemId);
}
export function isEditorOpen(itemId: string): boolean {
  return openFor === itemId;
}
/** Did an anomaly nudge ever fire for this line this session? Drives whether a
 *  saved note is recorded as 'anomaly' (prompted) or 'manual' (volunteered). */
export function wasPrompted(itemId: string): boolean {
  return promptedEver.has(itemId);
}

/** Fields whose hand-edit means the line was priced away from its calculated
 *  value. `retailPrice` and `tradePrice` are true overrides: recalcItem
 *  back-solves markup/discount from what was typed. `discPct` is a deviation
 *  from the supplier/range default the catalogue resolved. */
const OVERRIDE_FIELDS = new Set(["retailPrice", "tradePrice", "discPct"]);

/**
 * Decide whether a field change on a line is worth nudging about.
 *
 * Deliberately cheap: it reads the `changedField` the QB already passes to
 * recalcItem, so it needs no catalogue lookup and no notion of an "expected"
 * price beyond what the pricing chain already computes. See the note in the
 * build report about why the trigger list is short.
 */
export function isAnomalousEdit(field: string, item: QuoteItem, defaultMarkupPct: number): boolean {
  if (OVERRIDE_FIELDS.has(field)) return true;
  // Markup moved off the session default on a single line. `defaults.markupPct`
  // is the standard this quote is being priced to, so a line that leaves it is
  // a deviation by the quote's own definition.
  if (field === "markupPct" || field === "markupGbp") {
    const pct = item.markupPct || 0;
    return Math.abs(pct - (defaultMarkupPct || 0)) > 0.01;
  }
  return false;
}

/** Raise the nudge on a line. A previously dismissed line can nudge again on a
 *  NEW anomaly — dismissing clears the current prompt, it doesn't opt the line
 *  out for good. */
export function markAnomaly(itemId: string): void {
  if (!itemId) return;
  dismissed.delete(itemId);
  nudged.add(itemId);
  promptedEver.add(itemId);
}

export function dismissNudge(itemId: string): void {
  dismissed.add(itemId);
  nudged.delete(itemId);
}

export function toggleEditor(itemId: string): void {
  openFor = openFor === itemId ? null : itemId;
  // Opening the editor answers the nudge, so stop pulsing at it.
  if (openFor) nudged.delete(openFor);
}

export function closeEditor(): void {
  openFor = null;
}

// ── API ─────────────────────────────────────────────────────────────────────

/** Load everything captured on this quote. Best-effort: capture is an
 *  assistive surface, so a failure here must never block pricing. */
export async function loadForQuote(quoteId: string | undefined): Promise<void> {
  const key = quoteId || "";
  if (!key) { byItem.clear(); loadedQuoteKey = ""; return; }
  if (key === loadedQuoteKey) return;
  loadedQuoteKey = key;
  byItem.clear();
  try {
    const res = await fetch(`${BASE}/?quoteId=${encodeURIComponent(key)}`, { credentials: "include" });
    if (!res.ok) return;
    const data = await res.json().catch(() => null);
    for (const r of (data?.results ?? []) as RationaleRow[]) {
      if (r.itemId && !byItem.has(r.itemId)) byItem.set(r.itemId, r);
    }
  } catch {
    /* offline or 503 — the control still works, it just starts empty */
  }
}

export interface SaveCtx {
  // Explicitly `| undefined`: the repo runs with exactOptionalPropertyTypes,
  // and an unsaved quote genuinely has no serverId yet — qb_rationale allows
  // a null quote_id precisely so reasoning can be captured before first save.
  quoteId?: string | undefined;
  quoteRef?: string | undefined;
  zoneId: string;
  item: QuoteItem;
  /** 'anomaly' when answering a nudge — an answer to a prompt and an
   *  unprompted remark are different evidence to Kit. */
  source: "manual" | "anomaly";
}

/** Create or update the note on a line. Returns false on failure so the caller
 *  can keep the editor open rather than silently losing what was typed. */
export async function saveRationale(body: string, ctx: SaveCtx): Promise<boolean> {
  const text = body.trim();
  if (!text) return false;
  const existing = byItem.get(ctx.item.id);
  try {
    const res = existing
      ? await fetch(`${BASE}/${encodeURIComponent(existing.id)}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: text }),
        })
      : await fetch(`${BASE}/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            quoteId: ctx.quoteId || null,
            quoteRef: ctx.quoteRef || null,
            zoneId: ctx.zoneId,
            itemId: ctx.item.id,
            scope: "line",
            source: ctx.source,
            // The retrieval keys. subject_code is what Kit matches on first,
            // so it must be the catalogue code, not the free-typed description.
            subjectKind: ctx.item.category || null,
            subjectCode: ctx.item.code || null,
            subjectMeta: {
              supplier: ctx.item.supplier || null,
              desc: ctx.item.desc || null,
              width: ctx.item.width || null,
              doorRange: ctx.item.doorRange || null,
              doorColour: ctx.item.doorColour || null,
              discPct: ctx.item.discPct ?? null,
              markupPct: ctx.item.markupPct ?? null,
            },
            body: text,
          }),
        });
    if (!res.ok) return false;
    const data = await res.json().catch(() => null);
    if (!data?.ok) return false;
    byItem.set(ctx.item.id, { ...(data.rationale as RationaleRow), itemId: ctx.item.id });
    return true;
  } catch {
    return false;
  }
}

// ── Rendering ───────────────────────────────────────────────────────────────

/** The per-line control, for the row's action group. */
export function renderWhyButton(itemId: string): string {
  const has = hasRationale(itemId);
  const nudge = isNudged(itemId);
  const open = isEditorOpen(itemId);
  const cls = [
    "qb-icon-btn",
    "qb-why",
    has ? "qb-why--has" : "",
    nudge ? "qb-why--nudge" : "",
    open ? "qb-why--open" : "",
  ].filter(Boolean).join(" ");
  const title = has
    ? "Why: note recorded — click to edit"
    : nudge
      ? "This line was priced by hand — want to note why?"
      : "Why did you price it this way? (internal note)";
  return `<button type="button" class="${cls}" data-action="why" title="${title}"`
    + ` aria-label="${title}" aria-pressed="${open ? "true" : "false"}">${svgIcon(WHY_ICON)}</button>`;
}

/** Inline editor. Deliberately NOT a modal — it must not interrupt pricing,
 *  and the platform modal rule is about replacing window.alert/confirm, not
 *  about forcing every input into a dialog. */
export function renderEditor(itemId: string): string {
  if (!isEditorOpen(itemId)) return "";
  const existing = byItem.get(itemId);
  const wasNudged = nudged.has(itemId) || dismissed.has(itemId);
  return `
    <div class="qb-why-editor" data-role="why-editor" data-item="${itemId}">
      <div class="qb-why-editor__head">
        <span class="qb-why-editor__lbl">Why this price / choice?</span>
        <span class="qb-why-editor__hint">${wasNudged
          ? "Priced by hand — a line on the reasoning helps Kit later."
          : "Internal only. Never shown to the customer."}</span>
      </div>
      <textarea class="qb-why-editor__input" data-role="why-input" rows="2" maxlength="4000"
        placeholder="e.g. dropped to 5% margin to win the fitting work on this job"
        spellcheck="true">${escapeText(existing?.body ?? "")}</textarea>
      <div class="qb-why-editor__actions">
        <button type="button" class="qb-btn qb-btn--ghost" data-role="why-cancel">Cancel</button>
        <button type="button" class="qb-btn" data-role="why-save">Save note</button>
        <span class="qb-why-editor__status" data-role="why-status" aria-live="polite"></span>
      </div>
    </div>`;
}

/** Textarea content needs `<`/`&` escaped; it is not an attribute, so quotes
 *  can stay as typed. */
function escapeText(s: string): string {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
